import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService, messagingService } from '../../services/api';
import {
  FiHome, FiUsers, FiImage, FiCalendar, FiMessageSquare,
  FiBell, FiMenu, FiX, FiLogOut, FiSettings, FiUser,
  FiBook, FiShield, FiCamera, FiFolder
} from 'react-icons/fi';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import './Header.css';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUnreadCounts();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchUnreadCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCounts = async () => {
    try {
      const [notifResponse, msgResponse] = await Promise.all([
        notificationService.getUnreadCount(),
        messagingService.getUnreadCount()
      ]);

      if (notifResponse.success) {
        setUnreadNotifications(notifResponse.data);
      }
      if (msgResponse.success) {
        setUnreadMessages(msgResponse.data.totalUnreadMessages);
      }
    } catch (err) {
      console.error('Failed to fetch unread counts:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowMobileMenu(false);
    setShowProfileMenu(false);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo" onClick={closeMobileMenu}>
          <Logo className="logo-svg" />
        </Link>

        {/* Desktop Navigation */}
        {user && (
          <nav className="header-nav desktop-nav">
            <Link to="/" className={`nav-link ${isActive('/') && location.pathname === '/' ? 'active' : ''}`}>
              <FiHome />
              <span>Home</span>
            </Link>
            <Link to="/family" className={`nav-link ${isActive('/family') ? 'active' : ''}`}>
              <FiUsers />
              <span>Family</span>
            </Link>
            <Link to="/photos" className={`nav-link ${isActive('/photos') ? 'active' : ''}`}>
              <FiImage />
              <span>Photos</span>
            </Link>
            <Link to="/events" className={`nav-link ${isActive('/events') ? 'active' : ''}`}>
              <FiCalendar />
              <span>Events</span>
            </Link>
            <Link to="/stories" className={`nav-link ${isActive('/stories') ? 'active' : ''}`}>
              <FiBook />
              <span>Stories</span>
            </Link>
            <Link to="/recipes" className={`nav-link ${isActive('/recipes') ? 'active' : ''}`}>
              <FiBook />
              <span>Recipes</span>
            </Link>
          </nav>
        )}

        {/* Right Side Actions */}
        {user && (
          <div className="header-actions">
            <Link to="/messages" className="header-action-btn" title="Messages">
              <FiMessageSquare />
              {unreadMessages > 0 && (
                <span className="badge-count">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
              )}
            </Link>
            <Link to="/notifications" className="header-action-btn" title="Notifications">
              <FiBell />
              {unreadNotifications > 0 && (
                <span className="badge-count">{unreadNotifications > 99 ? '99+' : unreadNotifications}</span>
              )}
            </Link>

            {/* Profile Dropdown */}
            <div className="profile-dropdown">
              <button
                className="profile-btn"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                {user.profilePhotoURL ? (
                  <img src={user.profilePhotoURL} alt={user.firstName} className="avatar" />
                ) : (
                  <div className="avatar">
                    {getInitials(user.firstName, user.lastName)}
                  </div>
                )}
              </button>

              {showProfileMenu && (
                <>
                  <div className="dropdown-backdrop" onClick={() => setShowProfileMenu(false)} />
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      <strong>{user.firstName} {user.lastName}</strong>
                      <span>{user.email}</span>
                    </div>
                    <div className="dropdown-divider" />
                    <Link to="/profile" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                      <FiUser /> My Profile
                    </Link>
                    <Link to="/settings" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                      <FiSettings /> Settings
                    </Link>
                    {user.isAdmin && (
                      <Link to="/admin" className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                        <FiShield /> Admin Panel
                      </Link>
                    )}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item logout" onClick={handleLogout}>
                      <FiLogOut /> Log Out
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {showMobileMenu ? <FiX /> : <FiMenu />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      {user && showMobileMenu && (
        <>
          <div className="mobile-backdrop" onClick={closeMobileMenu} />
          <nav className="mobile-nav">
            <Link to="/" className={`mobile-nav-link ${isActive('/') && location.pathname === '/' ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiHome /> Home
            </Link>
            <Link to="/family" className={`mobile-nav-link ${isActive('/family') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiUsers /> Family
            </Link>
            <Link to="/photos" className={`mobile-nav-link ${isActive('/photos') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiImage /> Photos
            </Link>
            <Link to="/events" className={`mobile-nav-link ${isActive('/events') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiCalendar /> Events
            </Link>
            <Link to="/event-albums" className={`mobile-nav-link ${isActive('/event-albums') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiCamera /> Event Albums
            </Link>
            <Link to="/family-albums" className={`mobile-nav-link ${isActive('/family-albums') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiFolder /> Family Albums
            </Link>
            <Link to="/stories" className={`mobile-nav-link ${isActive('/stories') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiBook /> Stories
            </Link>
            <Link to="/recipes" className={`mobile-nav-link ${isActive('/recipes') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiBook /> Recipes
            </Link>
            <Link to="/messages" className={`mobile-nav-link ${isActive('/messages') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiMessageSquare /> Messages
              {unreadMessages > 0 && <span className="badge-count">{unreadMessages}</span>}
            </Link>
            <Link to="/notifications" className={`mobile-nav-link ${isActive('/notifications') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiBell /> Notifications
              {unreadNotifications > 0 && <span className="badge-count">{unreadNotifications}</span>}
            </Link>
            <div className="mobile-nav-divider" />
            <Link to="/profile" className={`mobile-nav-link ${isActive('/profile') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiUser /> My Profile
            </Link>
            <Link to="/settings" className={`mobile-nav-link ${isActive('/settings') ? 'active' : ''}`} onClick={closeMobileMenu}>
              <FiSettings /> Settings
            </Link>
            {user.isAdmin && (
              <Link to="/admin" className={`mobile-nav-link ${isActive('/admin') ? 'active' : ''}`} onClick={closeMobileMenu}>
                <FiShield /> Admin Panel
              </Link>
            )}
            <button className="mobile-nav-link logout" onClick={handleLogout}>
              <FiLogOut /> Log Out
            </button>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;
